import React from 'react';
import { Mail, Phone, AlertCircle } from 'lucide-react';
import SEO from '../components/SEO';

const GrievancePage = () => {
  return (
    <div className="min-h-screen pt-20">
      <SEO
        title="Grievance Redressal Cell | Equi Pulse"
        description="Equi Pulse is committed to providing fair, transparent, professional research and advisory services. Learn about our grievance redressal mechanism and complaint resolution process."
        url="/grievance"
      />
      
      <section className="relative py-20 bg-gradient-to-br from-slate-950 via-emerald-950 to-slate-900 overflow-hidden">
        <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1450101499163-c8848c66ca85?auto=format&fit=crop&w=1920')] bg-cover bg-center opacity-10" />
        
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-3xl mx-auto text-center">
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
              Grievance <span className="text-amber-400">Redressal Cell</span>
            </h1>
            <p className="text-lg text-gray-300">
              We are committed to resolving your concerns promptly and fairly
            </p>
          </div>
        </div>
      </section>

      <div className="container mx-auto px-4 py-12 max-w-5xl">
        <div className="prose prose-invert prose-amber max-w-none text-gray-300">
          <div className="bg-emerald-900/20 border border-emerald-500/30 p-6 rounded-xl mb-8">
            <div className="flex items-start gap-3">
              <AlertCircle className="w-6 h-6 text-emerald-400 mt-1 flex-shrink-0" aria-hidden="true" />
              <div>
                <p className="leading-relaxed">
                  <strong>EQUI PULSE</strong> is committed to providing fair, transparent, professional research and advisory services to its clients. This Grievance Redressal Policy is formulated to ensure timely and effective resolution of client complaints and to comply with applicable SEBI regulations and best industry practices.
                </p>
                <p className="mt-4">
                  Clients who have any complaints, concerns, or grievances relating to services provided by EQUI PULSE are encouraged to follow the grievance redressal mechanism outlined below. All grievances shall be handled promptly, confidentially, and impartially.
                </p>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
            {/* Level 1 */}
            <div className="bg-gradient-to-br from-slate-900 to-slate-800 border border-emerald-500/30 rounded-xl p-6">
              <div className="flex items-center justify-center w-12 h-12 bg-emerald-900/30 rounded-lg mb-4">
                <span className="text-2xl font-bold text-emerald-400">1</span>
              </div>
              <h2 className="text-xl font-bold text-emerald-400 mb-3">Level 1: Primary Grievance Redressal</h2>
              <p className="text-gray-300 text-sm mb-4">
                Investors are requested to first approach the support team for resolution of their grievance.
              </p>
              <p className="text-gray-300 text-sm mb-3">
                If the grievance is not resolved, investors may write with complete details to:
              </p>
              <a href="mailto:care@equipulseresearch.com" className="flex items-center gap-2 text-amber-400 hover:text-amber-300 text-sm">
                <Mail className="w-4 h-4" aria-hidden="true" />
                care@equipulseresearch.com
              </a>
              <div className="mt-4 pt-4 border-t border-emerald-700/30">
                <p className="text-xs text-gray-400"><strong>Timeline:</strong> Response within 72 working hours</p>
              </div>
            </div>

            {/* Level 2 */}
            <div className="bg-gradient-to-br from-slate-900 to-slate-800 border border-amber-500/30 rounded-xl p-6">
              <div className="flex items-center justify-center w-12 h-12 bg-amber-900/30 rounded-lg mb-4">
                <span className="text-2xl font-bold text-amber-400">2</span>
              </div>
              <h2 className="text-xl font-bold text-amber-400 mb-3">Level 2: Escalation to Compliance Officer</h2>
              <p className="text-gray-300 text-sm mb-4">
                If the investor is not satisfied with the resolution provided at Level 1, the grievance may be escalated to the Compliance Officer:
              </p>
              <div className="space-y-2 mb-4">
                <p className="text-sm"><strong className="text-emerald-400">Name:</strong> Mr. Siddharth Sharma</p>
                <p className="text-sm"><strong className="text-emerald-400">Designation:</strong> Compliance Officer</p>
                <a href="mailto:compliance@equipulseresearch.com" className="flex items-center gap-2 text-amber-400 hover:text-amber-300 text-sm">
                  <Mail className="w-4 h-4" aria-hidden="true" />
                  compliance@equipulseresearch.com
                </a>
              </div>
              <div className="mt-4 pt-4 border-t border-amber-700/30">
                <p className="text-xs text-gray-400"><strong>Timeline:</strong> Resolution within 10 working days</p>
              </div>
            </div>

            {/* Level 3 */}
            <div className="bg-gradient-to-br from-slate-900 to-slate-800 border border-emerald-500/30 rounded-xl p-6">
              <div className="flex items-center justify-center w-12 h-12 bg-emerald-900/30 rounded-lg mb-4">
                <span className="text-2xl font-bold text-emerald-400">3</span>
              </div>
              <h2 className="text-xl font-bold text-emerald-400 mb-3">Level 3: SEBI / Regulatory Escalation</h2>
              <p className="text-gray-300 text-sm mb-4">
                If the investor is still not satisfied with the resolution provided by EQUI PULSE, the grievance may be escalated through SEBI's centralized platforms:
              </p>
              <ul className="space-y-2 text-sm">
                <li>
                  <strong className="text-amber-400">SEBI SCORES Portal:</strong><br/>
                  <a href="https://scores.sebi.gov.in" target="_blank" rel="noopener noreferrer" className="text-amber-400 hover:text-amber-300">
                    SEBI SCORES Portal (scores.sebi.gov.in)
                  </a>
                </li>
                <li>
                  <strong className="text-amber-400">SMART ODR Platform:</strong><br/>
                  <a href="https://smartodr.in" target="_blank" rel="noopener noreferrer" className="text-amber-400 hover:text-amber-300">
                    SMART ODR Platform (smartodr.in)
                  </a>
                </li>
              </ul>
            </div>
          </div>

          <div className="bg-amber-900/20 border border-amber-500/30 p-6 rounded-xl">
            <h2 className="text-xl font-bold text-amber-400 mb-4">Important Disclosure</h2>
            <ul className="space-y-2">
              <li>• Investors are requested to follow the above-mentioned grievance redressal hierarchy for effective and timely resolution</li>
              <li>• Complaints not routed through the prescribed mechanism may remain unaddressed</li>
              <li>• EQUI PULSE maintains records of investor complaints and their resolution as per SEBI guidelines</li>
              <li>• Investors are advised to keep <strong>EQUI PULSE</strong> informed while lodging complaints on regulatory platforms</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GrievancePage;